// Module: UgcPhotoGridScreen
// Role: S11 UGC 5-photo grid — sequential per-photo upload with progress,
//       50 MB client input guard + JPEG re-encode (BUG-1304), platform capability gating (ADR-023).
// Uses:
//   - hooks/useFileUpload:useFileUpload
//   - api/flow-client:useArtefactPresign, useSubmitCheckpoint
//   - shared/limits:MAX_UGC_PHOTOS, MAX_PHOTO_INPUT_BYTES
//   - providers/PlatformProvider:PlatformContext
//   - lib/uploadTelemetry:createUploadFunnel, uploadTimeoutFor
//   - lib/imageResize:ImageResizeError
//   - screens/pantene/_uploadXHR:UploadTimeoutError
//   - atoms: Frame, IOSStatusBar, MiniHeader, StepStrip, InstructionCard, CtaBar, HintBar
// Used by: FlowScreen.tsx (case 'UGC_PHOTO')
//
// ADR-023: no window.Telegram access, no 'tg'|'vk'|'max'|'telegram' literals here.
// ADR-022: MAX_UGC_PHOTOS symmetric limit — see shared/limits.ts.
// FTR-1317: funnel telemetry replaces ad-hoc apiFetch beacons.
// Memory: File reference released after each sequential upload (GC-safe).

import { useContext, useRef, useState, useCallback } from 'react';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { Ic } from '../../../components/atoms/Ic';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_UGC_PHOTOS, MAX_PHOTO_INPUT_BYTES, FLOW_PHOTO_MAX_DIM } from '../../../shared/limits';
import { resizeImageToBlob, ImageResizeError } from '../../../lib/imageResize';
import { createUploadFunnel, uploadTimeoutFor } from '../../../lib/uploadTelemetry';
import { UploadTimeoutError } from '../../../screens/pantene/_uploadXHR';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

const SUCCESS_SOFT_BG = '#EFF7E9';

// ─── Types ───────────────────────────────────────────────────────────────────

type SlotState = 'done' | 'active' | 'pending';

interface TemplateProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

// ─── Slot rendering ──────────────────────────────────────────────────────────

interface PhotoSlotProps {
  index: number;
  state: SlotState;
  thumbUrl?: string;
  progress?: number | null;
  onTap: () => void;
}

function PhotoSlot({ index, state, thumbUrl, progress, onTap }: PhotoSlotProps) {
  const isActive = state === 'active';
  const isDone = state === 'done';

  const baseStyle: React.CSSProperties = {
    aspectRatio: '1',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isActive ? 'pointer' : 'default',
    position: 'relative',
    overflow: 'hidden',
    background: isDone ? SUCCESS_SOFT_BG : isActive ? P.terraSoft : S.tertiaryGroupedBg,
    border: `${isActive ? 2 : 1}px ${isActive ? 'solid' : 'dashed'} ${
      isDone ? P.success : isActive ? P.terracotta : P.line
    }`,
  };

  return (
    <div
      style={baseStyle}
      onClick={isActive ? onTap : undefined}
      role={isActive ? 'button' : undefined}
      aria-label={isActive ? `Загрузить фото ${index + 1}` : undefined}
      data-testid={`photo-slot-${index}`}
      data-slot-state={state}
    >
      {isDone && thumbUrl ? (
        <img
          src={thumbUrl}
          alt={`Фото ${index + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : isDone ? (
        <Ic name="check" size={16} color={P.success} />
      ) : isActive ? (
        <Ic name="plus" size={16} color={P.terraDark} />
      ) : (
        <span style={{ font: T.caption, fontWeight: 700, color: S.tertiaryLabel }}>
          {index + 1}
        </span>
      )}

      {/* Progress overlay for active uploading slot */}
      {isActive && progress !== null && progress !== undefined && progress > 0 && progress < 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: 'rgba(0,0,0,0.15)',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.round(progress * 100)}%`,
              backgroundColor: P.terracotta,
              transition: 'width 0.2s linear',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Screen ──────────────────────────────────────────────────────────────────

export function UgcPhotoGridScreen({
  checkpoint,
  checkpoints,
  attempts,
  flowRun,
}: TemplateProps) {
  // Platform capability check (ADR-023: null-safe, no platform literals)
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  // Derive accepted count from attempts prop (server truth after refetch).
  // Optimistic counter increments locally on submit success so the grid
  // advances before the parent refetch resolves (spec lines 482–545).
  const acceptedFromProps = attempts.filter(
    (a) => a.checkpoint_id === checkpoint.id && a.verdict === 'accepted',
  ).length;
  const [optimisticDone, setOptimisticDone] = useState(0);
  const doneCount = Math.max(acceptedFromProps, optimisticDone);

  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [thumbUrls, setThumbUrls] = useState<Record<number, string>>({});

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);

  // Hidden file input — only mounted when fileUpload is supported
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Derive slot states from doneCount
  const slotStates: SlotState[] = Array.from({ length: MAX_UGC_PHOTOS }, (_, i) => {
    if (i < doneCount) return 'done';
    if (i === doneCount) return 'active';
    return 'pending';
  });

  const parentCount = checkpoints.filter((c) => c.parent_checkpoint_id === null).length;
  const isComplete = doneCount >= MAX_UGC_PHOTOS;

  // Trigger file picker for the active slot
  const handleSlotTap = useCallback(() => {
    if (!canUpload) {
      setHintMessage('Загрузка файлов недоступна на этой платформе.');
      return;
    }
    if (isComplete) return;
    fileInputRef.current?.click();
  }, [canUpload, isComplete]);

  // Handle file picked from input
  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset input value so same file can be re-selected after error
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file) return;

      // BUG-1304: 50 MB input guard (raw file before re-encode)
      if (file.size > MAX_PHOTO_INPUT_BYTES) {
        setHintMessage('Файл слишком большой. Максимум 50 МБ.');
        return;
      }

      setHintMessage(null);
      const currentIndex = doneCount;

      // FTR-1317: upload funnel telemetry accumulator
      const funnel = createUploadFunnel(flowRun.slot_id, checkpoint.slug);
      funnel.stage('file_picked', { mime: file.type, size: file.size });

      // BUG-1304: normalize to JPEG
      let uploadFile = file;
      try {
        funnel.stage('resize_start');
        const blob = await resizeImageToBlob(file, { maxDim: FLOW_PHOTO_MAX_DIM, quality: 0.85 });
        uploadFile = new File([blob], 'upload.jpg', { type: 'image/jpeg' });
        funnel.stage('resize_done', { size: blob.size });
      } catch (resizeErr) {
        if (resizeErr instanceof ImageResizeError && resizeErr.code === 'decode_timeout') {
          funnel.stage('resize_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
          return;
        }
        funnel.stage('resize_failed', { code: resizeErr instanceof ImageResizeError ? resizeErr.code : undefined });
        console.warn('[FTR-1317] re-encode failed, uploading original:', resizeErr);
      }

      try {
        funnel.stage('presign_start');
        const presignResult = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: 'real_photo',
          content_md5: '',
          file_size_bytes: uploadFile.size,
        });
        funnel.stage('presign_done');

        funnel.stage('upload_start');
        await upload.start({ url: presignResult.upload_url, file: uploadFile, timeoutMs: uploadTimeoutFor(uploadFile.size) });
        funnel.stage('upload_done');

        const objectUrl = URL.createObjectURL(uploadFile);
        setThumbUrls((prev) => ({ ...prev, [currentIndex]: objectUrl }));

        funnel.stage('submit_start');
        await submitCheckpoint.mutateAsync({ artefact_path: presignResult.artefact_path });
        funnel.stage('submit_done');

        funnel.flush('success');
        setOptimisticDone((prev) => prev + 1);
      } catch (err: unknown) {
        const isTimeout = err instanceof UploadTimeoutError || (err instanceof DOMException && err.name === 'AbortError');
        if (isTimeout) {
          funnel.stage('upload_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
        } else {
          const msg =
            err instanceof ImageResizeError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Неизвестная ошибка';
          funnel.stage('upload_error');
          funnel.flush('failed');
          setHintMessage(`Ошибка загрузки: ${msg}`);
        }
      }
    },
    [doneCount, checkpoint.slug, flowRun.slot_id, presign, upload, submitCheckpoint],
  );

  return (
    <Frame>
      <MiniHeader title={flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip
          current={checkpoint.position + 1}
          total={parentCount}
          sub={{ current: doneCount, total: MAX_UGC_PHOTOS }}
          showCaption
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">

        {/* Title */}
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Пришли 5 фото товара
          </div>
          <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 4 }}>
            Реальные фото — не скрины с маркетплейса
          </div>
        </div>

        <InstructionCard p={12}>
          {/* Progress header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}>
            <div style={{ font: T.subhead, fontWeight: 600, color: S.label }}>
              {doneCount} из {MAX_UGC_PHOTOS} принято
            </div>
            {doneCount > 0 && (
              <div style={{ display: 'flex', gap: 3 }}>
                {Array.from({ length: doneCount }).map((_, i) => (
                  <Ic key={i} name="check" size={14} color={P.success} />
                ))}
              </div>
            )}
          </div>

          {/* 5-slot CSS grid (ADR-022: MAX_UGC_PHOTOS = 5) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${MAX_UGC_PHOTOS}, 1fr)`,
              gap: 6,
            }}
            data-testid="photo-grid"
          >
            {slotStates.map((state, i) => (
              <PhotoSlot
                key={i}
                index={i}
                state={state}
                thumbUrl={thumbUrls[i]}
                progress={state === 'active' ? upload.progress : null}
                onTap={handleSlotTap}
              />
            ))}
          </div>

          {/* Hint text */}
          <div style={{ font: T.captionAlt, color: S.secondaryLabel, marginTop: 10, lineHeight: 1.5 }}>
            {checkpoint.instructions_text ?? 'Можно одним альбомом — выдели 5 фото в галерее'}
          </div>
        </InstructionCard>

        {/* Requirements card */}
        <div style={{
          background: P.card, borderRadius: 14, padding: 12,
          border: `1px solid ${P.line}`,
        }}>
          <div style={{ font: T.caption, fontWeight: 700, color: S.label, marginBottom: 6 }}>
            Требования:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              'Реальное фото товара',
              'Крупный план, хорошее освещение',
              'Если одежда — в одежде, не мятой',
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ height: 19, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Ic name="check" size={14} color={P.success} />
                </div>
                <div style={{ flex: 1, font: T.footnote, color: S.label, lineHeight: '19px' }}>
                  {text}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Platform capability guard: non-TG platforms (ADR-023) */}
        {!canUpload && (
          <HintBar>Загрузка файлов недоступна на этой платформе.</HintBar>
        )}

        {/* Inline error/hint messages */}
        {hintMessage && canUpload && (
          <HintBar>{hintMessage}</HintBar>
        )}

        {isComplete && (
          <div style={{
            background: SUCCESS_SOFT_BG, borderRadius: 12, padding: '10px 12px',
            border: '1px solid rgba(45,138,78,0.25)',
            font: T.footnote, color: S.label, lineHeight: 1.45,
          }}>
            Все {MAX_UGC_PHOTOS} фото загружены. Отлично!
          </div>
        )}
      </div>

      {/* Hidden file input — ADR-023: no platform literals, just capability flag */}
      {canUpload && !isComplete && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="photo-input"
          aria-hidden="true"
        />
      )}

      <WizardCtaBar
        label={isComplete ? 'Готово' : `Фото ${doneCount + 1} из ${MAX_UGC_PHOTOS}`}
        onClick={handleSlotTap}
        disabled={isComplete || upload.status === 'uploading' || !canUpload}
      />
    </Frame>
  );
}
